import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, ViewEncapsulation, Input, HostBinding, Output, ViewChild, NgModule } from '@angular/core';
import { uniqueId } from '@eui/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@eui/components/eui-icon';
import { EuiIconModule } from '@eui/components/eui-icon';

class EuiIconToggleComponent {
    constructor() {
        this.id = `eui-icon-toggle-${uniqueId()}`;
        this.iconSvgFillColorOn = 'accent-100';
        this.iconSvgFillColorOff = 'grey-100';
        this.tabindex = 0;
        this.ariaLabel = 'Toggle icon';
        this.e2eAttr = 'eui-icon-toggle';
        this.toggle = new EventEmitter();
        this._isChecked = false;
        this._isReadOnly = false;
    }
    get isChecked() { return this._isChecked; }
    set isChecked(value) { this._isChecked = coerceBooleanProperty(value); }
    get isReadOnly() { return this._isReadOnly; }
    set isReadOnly(value) { this._isReadOnly = coerceBooleanProperty(value); }
    ngAfterContentInit() {
        this.setIconClass();
        if (!this.ariaLabel || this.ariaLabel === '') {
            this.ariaLabel = (this.iconClass ? this.iconClass : '');
        }
    }
    onToggle() {
        if (!this.isReadOnly) {
            this.isChecked = !this.isChecked;
            this.setIconClass();
            this.toggle.emit(this.isChecked);
        }
        this.focusElement.nativeElement.focus();
    }
    onKeyDown(event) {
        // eslint-disable-next-line import/no-deprecated
        switch (event.keyCode) {
            case 13: // ENTER
            case 32: // SPACE
                // consume event
                event.preventDefault();
                event.stopPropagation();
                event.cancelBubble = true;
                this.onToggle();
                break;
        }
    }
    setIconClass() {
        if (this.isChecked) {
            this.iconClass = this.iconClassOn;
            this.stateClass = this.styleClassOn;
            this.iconSvgName = this.iconSvgNameOn;
            this.iconSvgFillColor = this.iconSvgFillColorOn;
        }
        else {
            this.iconClass = this.iconClassOff;
            this.stateClass = this.styleClassOff;
            this.iconSvgName = this.iconSvgNameOff;
            this.iconSvgFillColor = this.iconSvgFillColorOff;
        }
    }
}
/** @nocollapse */ EuiIconToggleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiIconToggleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiIconToggleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiIconToggleComponent, selector: "eui-icon-toggle", inputs: { id: "id", styleClass: "styleClass", styleClassOn: "styleClassOn", styleClassOff: "styleClassOff", iconClassOff: "iconClassOff", iconClassOn: "iconClassOn", iconSvgNameOn: "iconSvgNameOn", iconSvgNameOff: "iconSvgNameOff", iconSvgFillColorOn: "iconSvgFillColorOn", iconSvgFillColorOff: "iconSvgFillColorOff", tabindex: "tabindex", ariaLabel: "ariaLabel", keyboardAccessKey: "keyboardAccessKey", e2eAttr: "e2eAttr", isChecked: "isChecked", isReadOnly: "isReadOnly" }, outputs: { toggle: "toggle" }, host: { properties: { "attr.data-e2e": "this.e2eAttr" } }, viewQueries: [{ propertyName: "focusElement", first: true, predicate: ["focusElement"], descendants: true }], ngImport: i0, template: "<div #focusElement [id]=\"id\"\n      class=\"eui-icon-toggle {{styleClass}}\"\n      [ngClass]=\"isChecked ? 'eui-icon-toggle--default-on' : 'eui-icon-toggle--default-off'\"\n      tabindex=\"{{!isReadOnly ? tabindex: '-1'}}\"\n      role=\"switch\"\n      [attr.aria-label]=\"ariaLabel\"\n      [attr.aria-checked]=\"isChecked\"\n      [attr.accesskey]=\"keyboardAccessKey\"\n      (click)=\"onToggle()\"\n      (keydown)=\"onKeyDown($event)\">\n\n    <ng-container *ngIf=\"iconClassOn && iconClassOff\">\n        <!-- V15 OK-->\n        <span class=\"eui-icon-toggle__icon eui-icon {{iconClass}}\"\n              [class.eui-icon-toggle--checked]=\"isChecked\"\n              [class.eui-icon-toggle--readonly]=\"isReadOnly\">\n        </span>\n    </ng-container>\n\n    <ng-container *ngIf=\"iconSvgNameOn && iconSvgNameOff\">\n        <eui-icon-svg [icon]=\"iconSvgName\" [fillColor]=\"iconSvgFillColor\"></eui-icon-svg>\n    </ng-container>\n</div>\n", styles: [".eui-icon-toggle{display:inline-block;position:relative;cursor:pointer}.eui-icon-toggle:active,.eui-icon-toggle:focus{outline:2px solid var(--eui-base-color-accessible-focus);outline-offset:-2px;-moz-outline-radius:var(--eui-base-border-radius);transition:outline .15s ease-in-out,border .15s ease-in-out}.eui-icon-toggle--default-on{color:var(--eui-base-color-accent-100)}.eui-icon-toggle--default-off{color:var(--eui-base-color-grey-100)}.eui-icon-toggle__icon{font-size:var(--eui-base-font-size-2xl);position:relative}.eui-icon-toggle--checked{color:var(--eui-base-color-accent-110)}.eui-icon-toggle--readonly{cursor:auto}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.EuiIconSvgComponent, selector: "eui-icon-svg, span[euiIconSvg], i[euiIconSvg]", inputs: ["icon", "size", "fillColor", "set", "ariaLabelledby", "role", "style", "iconUrl", "aria-label", "ariaHidden", "focusable", "isLoading"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiIconToggleComponent, decorators: [{
            type: Component,
            args: [{ selector: 'eui-icon-toggle', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<div #focusElement [id]=\"id\"\n      class=\"eui-icon-toggle {{styleClass}}\"\n      [ngClass]=\"isChecked ? 'eui-icon-toggle--default-on' : 'eui-icon-toggle--default-off'\"\n      tabindex=\"{{!isReadOnly ? tabindex: '-1'}}\"\n      role=\"switch\"\n      [attr.aria-label]=\"ariaLabel\"\n      [attr.aria-checked]=\"isChecked\"\n      [attr.accesskey]=\"keyboardAccessKey\"\n      (click)=\"onToggle()\"\n      (keydown)=\"onKeyDown($event)\">\n\n    <ng-container *ngIf=\"iconClassOn && iconClassOff\">\n        <!-- V15 OK-->\n        <span class=\"eui-icon-toggle__icon eui-icon {{iconClass}}\"\n              [class.eui-icon-toggle--checked]=\"isChecked\"\n              [class.eui-icon-toggle--readonly]=\"isReadOnly\">\n        </span>\n    </ng-container>\n\n    <ng-container *ngIf=\"iconSvgNameOn && iconSvgNameOff\">\n        <eui-icon-svg [icon]=\"iconSvgName\" [fillColor]=\"iconSvgFillColor\"></eui-icon-svg>\n    </ng-container>\n</div>\n", styles: [".eui-icon-toggle{display:inline-block;position:relative;cursor:pointer}.eui-icon-toggle:active,.eui-icon-toggle:focus{outline:2px solid var(--eui-base-color-accessible-focus);outline-offset:-2px;-moz-outline-radius:var(--eui-base-border-radius);transition:outline .15s ease-in-out,border .15s ease-in-out}.eui-icon-toggle--default-on{color:var(--eui-base-color-accent-100)}.eui-icon-toggle--default-off{color:var(--eui-base-color-grey-100)}.eui-icon-toggle__icon{font-size:var(--eui-base-font-size-2xl);position:relative}.eui-icon-toggle--checked{color:var(--eui-base-color-accent-110)}.eui-icon-toggle--readonly{cursor:auto}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }], styleClass: [{
                type: Input
            }], styleClassOn: [{
                type: Input
            }], styleClassOff: [{
                type: Input
            }], iconClassOff: [{
                type: Input
            }], iconClassOn: [{
                type: Input
            }], iconSvgNameOn: [{
                type: Input
            }], iconSvgNameOff: [{
                type: Input
            }], iconSvgFillColorOn: [{
                type: Input
            }], iconSvgFillColorOff: [{
                type: Input
            }], tabindex: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], keyboardAccessKey: [{
                type: Input
            }], e2eAttr: [{
                type: HostBinding,
                args: ['attr.data-e2e']
            }, {
                type: Input
            }], toggle: [{
                type: Output
            }], focusElement: [{
                type: ViewChild,
                args: ['focusElement']
            }], isChecked: [{
                type: Input
            }], isReadOnly: [{
                type: Input
            }] } });

class EuiIconToggleModule {
}
/** @nocollapse */ EuiIconToggleModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiIconToggleModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiIconToggleModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiIconToggleModule, declarations: [EuiIconToggleComponent], imports: [CommonModule, EuiIconModule], exports: [EuiIconToggleComponent] });
/** @nocollapse */ EuiIconToggleModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiIconToggleModule, imports: [CommonModule, EuiIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiIconToggleModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, EuiIconModule],
                    exports: [EuiIconToggleComponent],
                    declarations: [EuiIconToggleComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { EuiIconToggleComponent, EuiIconToggleModule };
